import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const AdvancedAnalysisPage = () => {
  const [files, setFiles] = useState({});
  const [video, setVideo] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState(0);
  const [stageText, setStageText] = useState('');
  const navigate = useNavigate();

  const stages = [
    "Analyzing the Case",
    "Analyzing the Bezel",
    "Analyzing the Crown",
    "Analyzing the Lugs (Side)",
    "Analyzing the Lugs (Under)",
    "Analyzing the Bracelet/Strap",
    "Analyzing the Hour, Minute, and Second Hands",
    "Analyzing the Caseback",
    "Analyzing the Buckle/Clasp (Outside)",
    "Analyzing the Buckle/Clasp (Inside)",
    "Analyzing the Video"
  ];

  const handleFileChange = (e) => {
    const { name, files } = e.target;
    setFiles((prev) => ({ ...prev, [name]: files[0] }));
  };

  const handleVideoChange = (e) => {
    const file = e.target.files[0];
    const videoDuration = file.duration; // Placeholder for actual duration check

    if (videoDuration > 65) {
      setError("Video File longer than 60s, Please keep it under max 62s");
      return;
    }

    setVideo(file);
  };

  const handleSubmit = async () => {
    if (Object.keys(files).length < 10 || !video) {
      setError('Please upload all required files and the video.');
      return;
    }

    setIsUploading(true);
    setError(null);

    // Simulate progress bar with stage updates
    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }

        const newProgress = prev + 1;
        const stageIndex = Math.floor((newProgress / 100) * stages.length);
        setStageText(stages[stageIndex] || '');

        return newProgress;
      });
    }, 50); // Adjust speed as needed

    try {
      const formData = new FormData();
      Object.entries(files).forEach(([key, file]) => {
        formData.append(key, file);
      });
      formData.append('video', video);

      const response = await axios.post('/api/advanced-analysis', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      navigate('/results', { state: { result: response.data } });
    } catch (err) {
      setError('Failed to upload files. Please try again.');
    } finally {
      setIsUploading(false);
    }
  };

  return (
    <div className="container mx-auto py-10">
      <h1 className="text-3xl font-bold mb-6">Advanced Analysis</h1>
      <p className="mb-4">Upload the required images and video for further analysis.</p>

      <div className="grid grid-cols-1 gap-4 mb-6">
        {['case', 'bezel', 'crown', 'lug_side', 'lug_under', 'bracelet', 'hands', 'caseback', 'buckle_outside', 'buckle_inside'].map((field) => (
          <div key={field}>
            <label className="block text-sm font-medium mb-2 capitalize">{field.replace('_', ' ')}</label>
            <input
              type="file"
              name={field}
              accept="image/*"
              onChange={handleFileChange}
              className="block w-full border rounded p-2"
            />
          </div>
        ))}

        <div>
          <label className="block text-sm font-medium mb-2">Dial Video</label>
          <input
            type="file"
            name="video"
            accept="video/*"
            onChange={handleVideoChange}
            className="block w-full border rounded p-2"
          />
        </div>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}

      {isUploading && (
        <div className="mb-4">
          <p className="text-blue-600 mb-2">{stageText}</p>
          <div className="w-full bg-gray-200 rounded-full h-4">
            <div
              className="bg-blue-600 h-4 rounded-full"
              style={{ width: `${progress}%` }}
            ></div>
          </div>
        </div>
      )}

      <button
        onClick={handleSubmit}
        disabled={isUploading}
        className="bg-blue-600 text-white py-2 px-4 rounded hover:bg-blue-700 disabled:opacity-50"
      >
        {isUploading ? 'Uploading...' : 'Send and Verify'}
      </button>
    </div>
  );
};

export default AdvancedAnalysisPage;